'use client'

import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import Link from 'next/link'
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  ArrowRightIcon,
  BeakerIcon,
  BoltIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ClipboardDocumentCheckIcon,
  HomeModernIcon,
  SparklesIcon,
  SwatchIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline'
import { BROWSE_LINKS, PRODUCT_LIBRARY } from './product-library'
import { TrackingEvents } from '../../../lib/analytics'
import { TrackedToolLink } from '../../../components/shared/ContextualToolEntry'

const QuickAssessment = dynamic(
  () => import('@/components/shared/QuickAssessment').then((module) => module.QuickAssessment),
  {
    loading: () => (
      <div className="rounded-[1rem] border border-water-border bg-water-canvas px-5 py-6 text-sm text-water-muted" role="status">
        Preparing the consultation form…
      </div>
    ),
  },
)

const CONCERN_OPTIONS = [
  {
    id: 'scale',
    title: 'Hard water and scale',
    description: 'White spots, stiff laundry, crusty faucets, dry skin.',
    icon: SparklesIcon,
    accent: 'sky',
  },
  {
    id: 'chlorine',
    title: 'Chlorine taste or smell',
    description: 'Pool-like taste, chemical smell, or shower water that feels harsh.',
    icon: SwatchIcon,
    accent: 'emerald',
  },
  {
    id: 'drinking',
    title: 'Taste or contaminants at the kitchen tap',
    description: 'This is about drinking-water quality, not whether you want to treat the whole home or just one tap.',
    icon: BeakerIcon,
    accent: 'cyan',
  },
  {
    id: 'staining',
    title: 'Staining, discolouration, or odour',
    description: 'Rusty stains, yellow or brown water, metallic odour, or rotten-egg smell.',
    icon: HomeModernIcon,
    accent: 'amber',
  },
  {
    id: 'bacteria',
    title: 'Bacteria or water-safety concern',
    description: 'You want a microorganism-protection path or confirmation that UV is appropriate.',
    icon: BoltIcon,
    accent: 'violet',
  },
  {
    id: 'not-sure',
    title: 'I am not fully sure',
    description: 'You want the best starting point without guessing alone.',
    icon: ClipboardDocumentCheckIcon,
    accent: 'slate',
  },
]

const WATER_SOURCE_OPTIONS = [
  {
    id: 'city',
    title: 'City water',
    description: 'Municipal water with chlorine or chemical treatment.',
    icon: HomeModernIcon,
  },
  {
    id: 'well',
    title: 'Well water',
    description: 'Private well, rural water, or cottage source.',
    icon: BoltIcon,
  },
  {
    id: 'not-sure',
    title: 'Not sure',
    description: 'I do not want the result to depend on a guess.',
    icon: ClipboardDocumentCheckIcon,
  },
]

const COVERAGE_OPTIONS = [
  {
    id: 'whole-home',
    title: 'Whole home',
    description: 'Treat the full house: showers, appliances, laundry, and every tap.',
    icon: HomeModernIcon,
  },
  {
    id: 'drinking-water',
    title: 'Drinking water only',
    description: 'Treat only the kitchen drinking-water point, not the rest of the house.',
    icon: BeakerIcon,
  },
  {
    id: 'both',
    title: 'Both whole home and drinking water',
    description: 'Treat the full house first, and also plan for better drinking water at the kitchen tap.',
    icon: SparklesIcon,
  },
]

const HOUSEHOLD_OPTIONS = [
  {
    id: '1-2',
    title: '1 to 2 people',
    description: 'Smaller household or lighter daily water use.',
    icon: UserGroupIcon,
  },
  {
    id: '3-5',
    title: '3 to 5 people',
    description: 'Typical family-size demand.',
    icon: UserGroupIcon,
  },
  {
    id: '6+',
    title: '6+ people',
    description: 'Higher peak demand or busier water usage.',
    icon: UserGroupIcon,
  },
]

const FINDER_SYSTEMS = [
  {
    label: 'Softening',
    src: '/images/site-refresh/softener/scale-shield-single-source-v1.png',
    alt: 'Water Doctor Scale-Shield water softener',
  },
  {
    label: 'Drinking Water',
    src: '/images/site-refresh/residential/como-r1000-source-v1.png',
    alt: 'Water Doctor COMO-R1000 reverse osmosis system',
  },
  {
    label: 'UV Safety',
    src: '/images/site-refresh/uv/pure-guard-uv-source-v1.jpg',
    alt: 'Water Doctor Pure-Guard UV system',
  },
]

const QUIZ_STORAGE_KEY = 'water_doctor_help_me_choose_v1'
const QUIZ_VERSION = 1
const EMPTY_ANSWERS = Object.freeze({
  concerns: [],
  waterSource: '',
  coverage: '',
  household: '',
})
const VALID_CONCERNS = new Set(CONCERN_OPTIONS.map(({ id }) => id))
const VALID_WATER_SOURCES = new Set(WATER_SOURCE_OPTIONS.map(({ id }) => id))
const VALID_COVERAGE = new Set(COVERAGE_OPTIONS.map(({ id }) => id))
const VALID_HOUSEHOLDS = new Set(HOUSEHOLD_OPTIONS.map(({ id }) => id))

function sanitizeQuizAnswers(value = {}) {
  return {
    concerns: Array.isArray(value.concerns)
      ? [...new Set(value.concerns.filter((item) => VALID_CONCERNS.has(item)))].slice(0, 2)
      : [],
    waterSource: VALID_WATER_SOURCES.has(value.waterSource) ? value.waterSource : '',
    coverage: VALID_COVERAGE.has(value.coverage) ? value.coverage : '',
    household: VALID_HOUSEHOLDS.has(value.household) ? value.household : '',
  }
}

function maxReachableStep(answers) {
  if (!answers.concerns.length) return 0
  if (!answers.waterSource) return 1
  if (!answers.coverage) return 2
  if (!answers.household) return 3
  return 4
}

const FAQS = [
  {
    question: 'Why does this guide sometimes still push me to book an assessment?',
    answer:
      'Because water treatment fit depends on more than symptoms alone. Well-water chemistry, flow rate, pre-filtration, plumbing layout, and installation constraints can all change the right answer. The guide gets you close fast, then points you to assessment when the risk of a wrong purchase is higher.',
  },
  {
    question: 'Can one system handle both hard water and chlorine?',
    answer:
      'Yes, but the design matters. For city-water homes with both scale and chlorine complaints, we prefer a setup that keeps the carbon tank separate from the softener section so the carbon media can be serviced on its own schedule instead of shortening the entire system’s useful life.',
  },
  {
    question: 'If I choose a whole-home system, do I still need reverse osmosis?',
    answer:
      'Sometimes. Whole-home systems address concerns across the house. A point-of-use drinking-water system may also fit when you have a separate, defined concern at the kitchen tap; compare the exact model’s documented performance before choosing it.',
  },
  {
    question: 'Can I shop directly after using this page?',
    answer:
      'Some lower-risk results include direct product links. Results involving well water, microorganisms, mixed concerns, or uncertain fit lead with an assessment so an expert can confirm the treatment approach first.',
  },
]

const QUESTION_STEPS = [
  {
    id: 'concerns',
    eyebrow: 'Step 1 of 4',
    title: 'What are you trying to fix?',
    description: 'Choose up to two water problems. This step is about symptoms, not about how much of the home you want to treat.',
  },
  {
    id: 'waterSource',
    eyebrow: 'Step 2 of 4',
    title: 'What water source are you on?',
    description: 'City and well water usually need different decision paths.',
  },
  {
    id: 'coverage',
    eyebrow: 'Step 3 of 4',
    title: 'How much of the home do you want to treat?',
    description: 'This step is about treatment scope: whole home, drinking-water only, or both.',
  },
  {
    id: 'household',
    eyebrow: 'Step 4 of 4',
    title: 'How much daily demand should we assume?',
    description: 'Household size helps us lean toward the right system format.',
  },
]

function getConcernLabel(id) {
  return CONCERN_OPTIONS.find((option) => option.id === id)?.title || id
}

function getCityComboPath(household) {
  const svttPrimary = household === '3-5'

  return {
    primary: svttPrimary ? PRODUCT_LIBRARY.wdsvtt : PRODUCT_LIBRARY.ultimateCombo,
    primaryReason: svttPrimary
      ? 'This is the lead recommendation for a typical family-size household because it gives you softening plus carbon treatment, while keeping the carbon tank separate so it can be serviced on its own schedule instead of shortening the entire system’s useful life.'
      : 'This is the safer primary fit when you need the scale-plus-chlorine path but also want 30K, 45K, or 60K sizing flexibility for the household.',
    secondary: svttPrimary ? PRODUCT_LIBRARY.ultimateCombo : PRODUCT_LIBRARY.wdsvtt,
    secondaryReason: svttPrimary
      ? 'Use the sized twin-tank conditioner as the backup if your home is on the heavier-use side of this range or you want more explicit capacity options.'
      : 'Keep WD SVTT as the backup if your household lands closer to its roughly 2-to-4-person sweet spot and you want the separate-carbon design.',
  }
}

function getUncertaintyBackup(answers, signals) {
  const { isWell, drinkingOnly, both, largerHousehold, hasStaining, hasBacteria, hasScale, hasChlorine } = signals

  if (drinkingOnly) {
    return {
      product: largerHousehold || both ? PRODUCT_LIBRARY.comoR1000 : PRODUCT_LIBRARY.gf3,
      reason:
        'If you want a likely category to review while the assessment is in progress, start with RO because your scope still sounds more like a drinking-water decision than a whole-home system.',
    }
  }

  if (isWell || hasBacteria) {
    if (hasStaining) {
      return {
        product: PRODUCT_LIBRARY.clariTasteIron,
        reason:
          'If you want a product path to review while you wait, start with iron-focused well-water treatment because staining and odour usually matter before normal softening.',
      }
    }

    return {
      product: PRODUCT_LIBRARY.uvStandard,
      reason:
        'If you want a product path to review while you wait, UV is the nearest likely category because safety and staging are usually the bigger question on uncertain well-water paths.',
    }
  }

  if (hasScale && !hasChlorine) {
    return {
      product: PRODUCT_LIBRARY.symphonyStandard,
      reason:
        'If you want a likely category to review before Water Doctor confirms the fit, the Scale-Shield line is the nearest hard-water path.',
    }
  }

  if (hasChlorine && !hasScale) {
    return {
      product: PRODUCT_LIBRARY.carbonFilter,
      reason:
        'If you want a likely category to review before Water Doctor confirms the fit, the whole-home carbon path is the nearest chlorine-first option.',
    }
  }

  const comboPath = getCityComboPath(answers.household)

  return {
    product: comboPath.primary,
    reason:
      'If you still want a product page to review while the assessment is in progress, this is the nearest likely whole-home path based on the signals you selected.',
  }
}

function getDrinkingWaterAddOn(household) {
  return household === '6+' ? PRODUCT_LIBRARY.comoR1000 : PRODUCT_LIBRARY.gf3
}

function buildCombinedResult(answers, signals) {
  const { isCity, isWell, hasScale, hasChlorine, hasStaining, hasBacteria } = signals
  const drinkingWaterAddOn = getDrinkingWaterAddOn(answers.household)
  let wholeHomeProduct = null
  let wholeHomeReason = ''

  if (isCity && hasScale && hasChlorine) {
    const comboPath = getCityComboPath(answers.household)
    wholeHomeProduct = comboPath.primary
    wholeHomeReason = comboPath.primaryReason
  } else if (isCity && hasScale) {
    wholeHomeProduct = PRODUCT_LIBRARY.symphonyStandard
    wholeHomeReason = 'Use a properly sized softener as the whole-home foundation because hard water is the main house-wide problem you selected.'
  } else if (isCity && hasChlorine) {
    wholeHomeProduct = PRODUCT_LIBRARY.carbonFilter
    wholeHomeReason = 'Use whole-home carbon as the foundation because chlorine taste, odour, and shower comfort are the main house-wide concerns you selected.'
  } else if (isWell && hasStaining) {
    wholeHomeProduct = PRODUCT_LIBRARY.clariTasteIron
    wholeHomeReason = 'Start by confirming iron, sulphur, or tannin treatment for the whole home; the drinking-water system comes after the source-water problem is controlled.'
  } else if (isWell && hasBacteria) {
    wholeHomeProduct = answers.household === '6+' ? PRODUCT_LIBRARY.uvHighFlow : PRODUCT_LIBRARY.uvStandard
    wholeHomeReason = 'Use the UV category as the safety foundation, but confirm flow rate and the required pre-filtration before selecting the final model.'
  } else if (isWell && hasScale) {
    wholeHomeProduct = PRODUCT_LIBRARY.symphonyStandard
    wholeHomeReason = 'A softener may be the whole-home foundation, but a well-water test should confirm that iron, sediment, or other pretreatment is not needed first.'
  }

  if (!wholeHomeProduct) {
    return {
      headline: 'Confirm the whole-home problem before building a 2-part system.',
      summary:
        'You want both whole-home treatment and better drinking water, but the answers do not yet identify what the whole-home system must remove. Confirm that first, then add the right RO system at the kitchen tap.',
      primary: PRODUCT_LIBRARY.assessmentLead,
      primaryReason:
        'Water Doctor should confirm the house-wide issue before recommending softening, carbon, UV, or specialty filtration.',
      secondary: drinkingWaterAddOn,
      secondaryReason:
        'RO is the likely drinking-water component, but it should be paired with the correct whole-home foundation rather than treated as the complete plan.',
      secondaryRole: 'Drinking-water component to review',
      assessmentPriority: 'high',
      assessmentCopy:
        'Request the free assessment before purchase so the whole-home category and drinking-water add-on are planned together.',
      pairingCopy:
        'The final recommendation should contain 2 clear parts: the right whole-home treatment first, then a dedicated kitchen drinking-water system.',
      expertFirst: true,
      planType: 'paired',
    }
  }

  return {
    headline: 'A 2-part whole-home and drinking-water plan fits your goals.',
    summary:
      'One system should address the house-wide problem, while a separate point-of-use system can address a defined drinking-water concern at the kitchen tap.',
    primary: wholeHomeProduct,
    primaryReason: wholeHomeReason,
    primaryRole: 'Whole-home foundation',
    secondary: drinkingWaterAddOn,
    secondaryReason:
      answers.household === '6+'
        ? 'Use the higher-output tankless RO path for the drinking-water side of the plan because you selected heavier household demand.'
        : 'Use a dedicated RO system at the kitchen tap instead of expecting the whole-home equipment to deliver the same drinking-water result.',
    secondaryRole: 'Drinking-water add-on',
    assessmentPriority: isWell ? 'high' : 'recommended',
    assessmentCopy: isWell
      ? 'Confirm source-water chemistry, pre-filtration, flow, and installation before purchasing either part of this plan.'
      : 'Confirm final sizing and installation so the whole-home system and RO add-on are planned as one project.',
    pairingCopy:
      'Treat these as complementary systems with different jobs, not as 2 alternatives competing for the same purchase.',
    planType: 'paired',
  }
}

export function buildResult(answers) {
  const concerns = new Set(answers.concerns)
  const isCity = answers.waterSource === 'city'
  const isWell = answers.waterSource === 'well'
  const sourceUnknown = answers.waterSource === 'not-sure'
  const drinkingOnly = answers.coverage === 'drinking-water'
  const both = answers.coverage === 'both'
  const largerHousehold = answers.household === '6+'
  const familyHousehold = answers.household === '3-5' || answers.household === '6+'
  const hasScale = concerns.has('scale')
  const hasChlorine = concerns.has('chlorine')
  const hasDrinking = concerns.has('drinking')
  const hasStaining = concerns.has('staining')
  const hasBacteria = concerns.has('bacteria')
  const unsure = concerns.has('not-sure') || concerns.size === 0
  const uncertainFlow = unsure || sourceUnknown

  if (uncertainFlow) {
    const backup = getUncertaintyBackup(answers, {
      isWell,
      drinkingOnly,
      both,
      largerHousehold,
      hasStaining,
      hasBacteria,
      hasScale,
      hasChlorine,
    })
    const needsDiagnosticOnly = hasStaining || hasBacteria

    return {
      headline: 'You are in a confirm-before-you-buy path.',
      summary: sourceUnknown
        ? 'Because the water source is still uncertain, the biggest risk is choosing the wrong treatment category before you even start sizing products.'
        : 'Because you selected that you are not fully sure, the safest recommendation is to confirm the category first instead of forcing a product decision too early.',
      primary: PRODUCT_LIBRARY.assessmentLead,
      primaryReason: sourceUnknown
        ? 'When city water versus well water is still unclear, Water Doctor should confirm the source first because that changes whether the right path is carbon, softening, UV, iron treatment, or something else.'
        : 'When the symptom picture is still mixed, the home assessment is the fastest way to avoid buying the wrong type of system and then trying to size the wrong category.',
      secondary: needsDiagnosticOnly ? null : backup.product,
      secondaryReason: needsDiagnosticOnly ? null : backup.reason,
      assessmentPriority: 'high',
      assessmentCopy:
        'This is one of the strongest cases for submitting the home assessment first. It turns mixed symptoms into a clearer product path and sizing recommendation.',
      pairingCopy: needsDiagnosticOnly
        ? 'Share any existing water test and note whether the symptom is limited to hot water, cold water, or individual fixtures. Do not select equipment from the symptom alone.'
        : 'If you still want to self-educate while waiting, use the backup recommendation as the nearest category to review, not as a final answer.',
      expertFirst: true,
    }
  }

  if (hasBacteria || hasStaining) {
    const isMunicipalRisk = isCity

    return {
      headline: isMunicipalRisk
        ? 'Confirm the source of this city-water symptom before choosing equipment.'
        : 'Test and stage this well-water path before choosing equipment.',
      summary: hasBacteria
        ? isMunicipalRisk
          ? 'A bacteria concern on a municipal supply should be confirmed before treating the home as if it were a private-well UV project.'
          : 'A bacteria concern on well water can require pretreatment plus correctly sized UV, so source-water testing and peak flow should come before a product decision.'
        : isMunicipalRisk
          ? 'Discolouration or staining on city water can come from premise plumbing, the water heater, distribution work, or another property-specific source.'
          : 'Staining or odour on well water can involve iron, sulphur, tannins, sediment, or more than one treatment stage, which cannot be separated reliably from symptoms alone.',
      primary: PRODUCT_LIBRARY.assessmentLead,
      primaryReason:
        isMunicipalRisk
          ? 'Start with an expert review so Water Doctor can separate a treatment need from a plumbing, fixture, hot-water, or municipal issue.'
          : 'Start with an expert review so Water Doctor can confirm the water chemistry, required pretreatment, flow, and safe order of treatment stages.',
      secondary: null,
      secondaryReason: null,
      assessmentPriority: 'high',
      assessmentCopy:
        'Request the free assessment before purchase. A specific product recommendation would be premature from these answers alone.',
      pairingCopy: isMunicipalRisk
        ? 'If the issue began suddenly or affects nearby properties, contact the municipality first. If it is isolated to the property, check hot versus cold water and individual fixtures.'
        : 'Share any existing lab results and note whether the symptom appears in hot water, cold water, or both. Those details help determine the correct treatment sequence.',
      expertFirst: true,
      planType: answers.coverage === 'both' ? 'paired' : 'single',
    }
  }

  const onlyDrinkingConcern = hasDrinking && concerns.size === 1
  if (
    answers.coverage === 'whole-home' &&
    ((isCity && onlyDrinkingConcern) || (isWell && (hasChlorine || onlyDrinkingConcern)))
  ) {
    return {
      headline: 'Confirm the treatment goal before choosing a whole-home product.',
      summary: isWell && hasChlorine
        ? 'A chlorine concern on well water can reflect an existing disinfection process or another source-specific condition; it should not be translated into a standard softener recommendation.'
        : 'You selected a drinking-water concern but whole-home coverage. Confirm whether the goal is the kitchen tap, the full plumbing system, or both before choosing equipment.',
      primary: PRODUCT_LIBRARY.assessmentLead,
      primaryReason:
        'Water Doctor should confirm the source, treatment scope, and water-quality target before recommending softening, carbon, RO, UV, or specialty filtration.',
      secondary: null,
      secondaryReason: null,
      assessmentPriority: 'high',
      assessmentCopy:
        'Request the free assessment before purchase so the recommendation solves the right problem at the right point in the home.',
      pairingCopy:
        'A whole-home system and a kitchen drinking-water system do different jobs. The final plan should name which job each treatment stage is meant to do.',
      expertFirst: true,
      planType: 'single',
    }
  }

  if (both) {
    return buildCombinedResult(answers, {
      isCity,
      isWell,
      hasScale,
      hasChlorine,
      hasStaining,
      hasBacteria,
    })
  }

  if (isWell && !drinkingOnly && !hasScale && !hasChlorine && !hasStaining && !hasDrinking) {
    const uvPrimary = largerHousehold ? PRODUCT_LIBRARY.uvHighFlow : PRODUCT_LIBRARY.uvStandard
    const uvBackup = largerHousehold ? PRODUCT_LIBRARY.uvStandard : PRODUCT_LIBRARY.uvHighFlow

    return {
      headline: 'You are in a UV-first path.',
      summary:
        'Your answers lean toward microorganism protection or a well-water safety decision, which is usually more about correct sizing and pre-filtration than a fast cart push.',
      primary: uvPrimary,
      primaryReason:
        largerHousehold
          ? 'With higher household demand, it is safer to start by reviewing the higher-flow UV configuration on the same Pure-Guard platform.'
          : 'UV is the clearest fit when safety and microorganism protection matter more than taste or scale.',
      secondary: hasStaining ? PRODUCT_LIBRARY.clariTasteIron : uvBackup,
      secondaryReason:
        hasStaining
          ? 'Because you also flagged staining or odour, keep iron and sulphur treatment in view as the likely pre-filtration step before UV.'
          : largerHousehold
            ? 'Use the standard-flow model as the backup only if your actual peak demand is lower than expected after Water Doctor reviews it.'
            : 'Keep the higher-flow configuration in reserve if your home has busier peak water demand than a standard setup should handle.',
      assessmentPriority: 'high',
      assessmentCopy:
        'Book the assessment before buying if there is any uncertainty around flow rate, pre-filtration, or well-water chemistry. UV performance depends on those details.',
      pairingCopy:
        hasDrinking || both
          ? 'If you also have a defined kitchen drinking-water concern, evaluate point-of-use treatment after the safety path is confirmed.'
          : null,
    }
  }

  if (drinkingOnly) {
    if (largerHousehold) {
      return {
        headline: 'You are in a drinking-water-first path.',
        summary:
          'The answers point more toward point-of-use water quality than a whole-home treatment project, so reverse osmosis is the right place to start.',
        primary: PRODUCT_LIBRARY.comoR1000,
        primaryReason:
          'The COMO-R1000 is the stronger fit when a tankless layout and faster fill speed suit heavier daily kitchen use.',
        secondary: PRODUCT_LIBRARY.gf3,
        secondaryReason:
          'Keep the GF3 as the backup if you still want high-output RO but prefer a different performance profile.',
        assessmentPriority: 'recommended',
        assessmentCopy:
          'Assessment is optional here, but still useful if you are deciding between point-of-use RO and a broader whole-home treatment path.',
        pairingCopy: null,
      }
    }

    return {
      headline: 'You are in a drinking-water-first path.',
      summary:
        'The problem sounds focused on taste, contaminants, or kitchen use, so a dedicated RO system makes more sense than starting with a whole-home unit.',
      primary: PRODUCT_LIBRARY.gf3,
      primaryReason:
        'The GF3 is the best all-around starting point when the goal is better drinking water without overcomplicating the rest of the home.',
      secondary: PRODUCT_LIBRARY.ros4a,
      secondaryReason:
        'Choose the 4-stage RO as the backup if you want a more classic under-sink format and do not need the larger-output or tankless path.',
      assessmentPriority: 'optional',
      assessmentCopy:
        'You can usually shop directly from here. Book the assessment only if you suspect your whole-home water issues are bigger than the kitchen tap alone.',
      pairingCopy: null,
    }
  }

  if (isCity && hasScale && hasChlorine) {
    const comboPath = getCityComboPath(answers.household)

    return {
      headline: 'You are in the city-water combo path.',
      summary:
        'Your answers say this is not just a hard-water problem or just a chlorine problem. You likely want one whole-home path that handles both.',
      primary: comboPath.primary,
      primaryReason: comboPath.primaryReason,
      secondary: comboPath.secondary,
      secondaryReason: comboPath.secondaryReason,
      assessmentPriority: 'recommended',
      assessmentCopy:
        'Assessment helps confirm plumbing access, drain location, and the final installation layout. Your answers already point to a strong product category to compare.',
      pairingCopy:
        both || hasDrinking
          ? 'If you also have a defined drinking-water concern at the sink, evaluate point-of-use treatment separately instead of expecting one whole-home system to do everything.'
          : null,
    }
  }

  if (isCity && hasChlorine && !hasScale) {
    return {
      headline: 'You are in a chlorine-removal-first path.',
      summary:
        'This looks more like a city-water chlorine and odour problem than a hardness problem, so a simpler whole-home carbon path is the cleaner first recommendation.',
      primary: PRODUCT_LIBRARY.carbonFilter,
      primaryReason:
        'Start here when the issue is chlorine taste, smell, or shower comfort and you are not seeing strong scale symptoms.',
      secondary: PRODUCT_LIBRARY.ultimateSaltFree,
      secondaryReason:
        'Keep the salt-free combo as the backup if you also want some scale prevention without moving into a salt-based softener path.',
      assessmentPriority: answers.household === '6+' ? 'recommended' : 'optional',
      assessmentCopy:
        answers.household === '6+'
          ? 'Use the assessment to confirm capacity because this path has fewer size choices than some other whole-home systems.'
          : 'You can often shop directly from here. Use assessment if you want Water Doctor to confirm whether a softener should also be part of the plan.',
      pairingCopy:
        hasDrinking || both
          ? 'A whole-home carbon filter addresses the house-wide concern; evaluate a separate point-of-use system only if the kitchen tap has another treatment goal.'
          : null,
    }
  }

  if (hasScale) {
    return {
      headline: 'You are in a hard-water protection path.',
      summary:
        'Scale and hardness look like the main issue, so a standard residential softener path makes more sense than the more specialized chlorine-combo systems.',
      primary: PRODUCT_LIBRARY.symphonyStandard,
      primaryReason:
        familyHousehold
          ? 'The Scale-Shield line is the better default because it comes in 30K, 45K, and 60K capacities, so the system can be sized to the household instead of forcing a one-size-fits-all answer.'
          : 'The Scale-Shield line is the cleaner starting point when hard water is the real problem and you want a normal softener, not a chlorine-focused combo.',
      secondary: PRODUCT_LIBRARY.symphonyLite,
      secondaryReason:
        'Use the Scale-Shield all-in-one model as the backup if you want an integrated format instead of the standard single-tank setup.',
      assessmentPriority: isWell ? 'high' : familyHousehold ? 'recommended' : 'optional',
      assessmentCopy:
        isWell
          ? 'Confirm the well-water chemistry and any pretreatment needs before selecting or sizing the softener.'
          : familyHousehold
          ? 'Assessment is worth using so Water Doctor can confirm the right softener capacity and install fit for the household.'
          : 'Your answers point to a clear product category. Assessment becomes more valuable if plumbing access, installation space, or future chlorine concerns are still unclear.',
      pairingCopy:
        both || hasDrinking
          ? 'For better drinking water at the sink, add RO later instead of treating the softener like a kitchen filtration system.'
          : null,
    }
  }

  return {
    headline: 'You are in a whole-home starting path.',
    summary:
      'Your answers do not point to a pure drinking-water-only problem, so the better first move is to look at the strongest whole-home category fit.',
    primary: isCity ? getCityComboPath(answers.household).primary : PRODUCT_LIBRARY.symphonyStandard,
    primaryReason:
      isCity
        ? getCityComboPath(answers.household).primaryReason
        : 'When the path is still broad but not drinking-water-only, the Scale-Shield softener line is the cleaner whole-home starting point for a normal hardness-led decision.',
    secondary: isCity ? getCityComboPath(answers.household).secondary : PRODUCT_LIBRARY.wdsvtt,
    secondaryReason:
      isCity
        ? getCityComboPath(answers.household).secondaryReason
        : 'Keep WD SVTT as the backup if chlorine removal also turns out to be part of the final plan rather than pure hard-water treatment alone.',
    assessmentPriority: 'recommended',
    assessmentCopy:
      'Use the assessment if the issue is still ambiguous. It is the fastest way to avoid solving the wrong problem first.',
    pairingCopy: null,
  }
}

function ProductLink({ product, className, children, dataUtmContent, ariaLabel }) {
  const mergedClassName = [
    className,
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue focus-visible:ring-offset-2',
  ]
    .filter(Boolean)
    .join(' ')

  const commonProps = {
    className: mergedClassName,
    'aria-label': ariaLabel,
    onClick: () => TrackingEvents.trackDiagnosticTools('help_me_choose_product_click', {
      tool: 'help-me-choose',
      product: product.shopifyHandle || product.title,
      destination: product.internal ? 'assessment' : 'shopify',
    }),
  }

  if (dataUtmContent) {
    commonProps['data-utm-content'] = dataUtmContent
  }

  if (product.internal) {
    return (
      <Link href={product.href} {...commonProps}>
        {children}
      </Link>
    )
  }

  return (
    <a href={product.href} {...commonProps}>
      {children}
    </a>
  )
}

function ProductCard({ product, reason, tone, compact = false, shopifyProduct, heading: headingOverride }) {
  const isPrimary = tone === 'primary'
  const badgeTone = isPrimary
    ? 'bg-water-ink text-white'
    : 'bg-water-sand text-water-ink'
  const heading = headingOverride || (isPrimary ? 'Best-fit starting point' : 'Alternative to compare')
  const highlights = product.highlights.slice(0, compact ? 2 : 3)
  const image = shopifyProduct?.images?.[0]
  const price = shopifyProduct?.startingPrice
  const originalPrice = shopifyProduct?.originalPrice
  const hasLiveShopifyProduct = Boolean(shopifyProduct?.title || image || price)
  const titleClass = compact
    ? 'text-[1.4rem] sm:text-[1.55rem]'
    : 'text-[1.7rem] sm:text-[1.9rem]'
  const mediaTileClass = compact
    ? 'aspect-square w-28 rounded-[1rem] p-3 sm:w-32'
    : 'aspect-square w-36 rounded-[1rem] p-3 sm:w-40'
  const imageClass = compact
    ? 'h-auto w-auto max-h-full max-w-full object-contain transition-transform duration-300 group-hover:scale-[1.04]'
    : 'h-auto w-auto max-h-full max-w-full object-contain transition-transform duration-300 group-hover:scale-[1.04]'
  const ctaClass = `inline-flex min-h-11 items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
    tone === 'primary'
      ? 'bg-water-blue text-white hover:bg-water-blue-dark'
      : 'border border-water-border bg-white text-water-ink hover:border-water-blue/35 hover:text-water-blue-dark'
  }`
  const pricePillClass = compact
    ? 'inline-flex items-baseline gap-2 tabular-nums'
    : 'inline-flex items-baseline gap-2 tabular-nums'
  const categoryClass = compact
    ? 'text-[0.8125rem] font-semibold uppercase tracking-[0.08em] text-water-blue-dark'
    : 'text-[0.8125rem] font-semibold uppercase tracking-[0.08em] text-water-blue-dark'
  const headerGridClass = compact
    ? 'mt-5 grid gap-4 sm:grid-cols-[auto_minmax(0,1fr)] sm:items-center'
    : 'mt-5 grid gap-5 sm:grid-cols-[auto_minmax(0,1fr)] sm:items-center'
  const actionWrapClass = compact
    ? 'mt-4 flex flex-col items-start gap-3'
    : 'mt-5 flex flex-wrap items-center gap-3'

  return (
    <article
      className={`relative flex h-full flex-col overflow-hidden rounded-[1.375rem] border ${
        isPrimary
          ? 'border-water-blue/30 bg-water-canvas shadow-[0_16px_40px_rgba(11,34,57,0.07)]'
          : 'border-water-border bg-white'
      }`}
    >
      <div className="relative flex h-full flex-col p-5 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className={`inline-flex rounded-full px-3 py-1.5 text-[0.8125rem] font-semibold ${badgeTone}`}>
            {heading}
          </span>
          {!product.internal && typeof shopifyProduct?.available === 'boolean' ? (
            <span
              className={`rounded-full px-3 py-1.5 text-[0.8125rem] font-semibold ${
                shopifyProduct.available
                  ? 'bg-water-aqua text-water-blue-dark'
                  : 'bg-amber-50 text-amber-800'
              }`}
            >
              {shopifyProduct.available ? 'Available' : 'Check stock'}
            </span>
          ) : null}
        </div>

        <div className={headerGridClass}>
          {hasLiveShopifyProduct ? (
            <ProductLink
              product={product}
              dataUtmContent={`help_me_choose_${tone}_${product.shopifyHandle || product.cta.toLowerCase().replace(/\s+/g, '_')}_image`}
              ariaLabel={`View ${product.title} on Shopify`}
              className={`group relative overflow-hidden border border-water-border bg-white ${mediaTileClass}`}
            >
              <div className="flex h-full w-full items-center justify-center">
                {image ? (
                  <Image
                    src={image.url}
                    alt={image.altText || shopifyProduct.title || product.title}
                    width={image.width || 900}
                    height={image.height || 900}
                    className={`${imageClass} motion-reduce:transition-none`}
                    sizes="(max-width: 640px) 128px, 160px"
                  />
                ) : (
                  <span className="text-5xl font-semibold text-water-border" aria-hidden="true">W</span>
                )}
              </div>
            </ProductLink>
          ) : null}

          <div className="min-w-0">
            <p className={categoryClass}>
              {product.category}
            </p>
            <ProductLink
              product={product}
              dataUtmContent={`help_me_choose_${tone}_${product.shopifyHandle || product.cta.toLowerCase().replace(/\s+/g, '_')}_title`}
              ariaLabel={`Open ${product.title}`}
              className="inline-block"
            >
              <h3 className={`mt-2 text-balance font-semibold leading-[1.08] tracking-[-0.03em] text-water-ink transition-colors hover:text-water-blue-dark ${titleClass}`}>
                {product.title}
              </h3>
            </ProductLink>

            <div className={actionWrapClass}>
              {price ? (
                <div className={pricePillClass}>
                  {shopifyProduct?.variantCount > 1 ? (
                    <span className="text-[0.8125rem] font-semibold text-water-muted">
                      From
                    </span>
                  ) : null}
                  <span className="text-2xl font-semibold tracking-tight text-water-ink">{price}</span>
                  {originalPrice ? (
                    <span className="text-sm text-water-muted line-through">{originalPrice}</span>
                  ) : null}
                </div>
              ) : null}

              <ProductLink
                product={product}
                dataUtmContent={`help_me_choose_${tone}_${product.shopifyHandle || product.cta.toLowerCase().replace(/\s+/g, '_')}_cta`}
                ariaLabel={product.cta}
                className={compact ? `${ctaClass} w-full sm:w-auto` : ctaClass}
              >
                {product.cta}
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
              </ProductLink>
            </div>
          </div>
        </div>

        <div className={`border-t border-water-border pt-5 ${compact ? 'mt-5' : 'mt-6'}`}>
          <p className="text-[0.8125rem] font-semibold uppercase tracking-[0.08em] text-water-blue-dark">
            Why this fits
          </p>
          <p className={`mt-2 text-water-muted ${compact ? 'text-sm leading-6' : 'text-base leading-7'}`}>
            {reason}
          </p>
        </div>

        <ul className={`${compact ? 'mt-4' : 'mt-5'} space-y-2.5`}>
          {highlights.map((highlight) => (
            <li key={highlight} className="flex items-start gap-2.5 text-sm leading-6 text-water-muted">
              <CheckCircleIcon className="mt-0.5 h-5 w-5 flex-none text-water-teal" aria-hidden="true" />
              <span>{highlight}</span>
            </li>
          ))}
        </ul>
      </div>
    </article>
  )
}

function QuizOptionCard({ option, selected, disabled, onClick }) {
  const Icon = option.icon

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      className={`group relative w-full rounded-[1.125rem] border p-4 text-left transition-[border-color,background-color,box-shadow,transform] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue focus-visible:ring-offset-2 motion-reduce:transition-none sm:p-5 ${
        selected
          ? 'border-water-blue bg-water-aqua shadow-[0_12px_30px_rgba(11,93,147,0.10)]'
          : 'border-water-border bg-white hover:-translate-y-0.5 hover:border-water-blue/35 hover:shadow-[0_12px_30px_rgba(11,34,57,0.07)]'
      } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
    >
      <div className="flex items-start gap-3.5 sm:gap-4">
        <div className={`flex h-11 w-11 flex-none items-center justify-center rounded-[0.875rem] transition-colors ${selected ? 'bg-water-blue text-white' : 'bg-water-canvas text-water-blue-dark group-hover:bg-water-aqua'}`}>
          <Icon className="h-5 w-5" aria-hidden="true" />
        </div>
        <div className="min-w-0">
          <div className="flex items-start justify-between gap-3">
            <h3 className="text-base font-semibold leading-6 tracking-[-0.01em] text-water-ink sm:text-lg">{option.title}</h3>
            {selected ? (
              <CheckCircleIcon className="mt-0.5 h-5 w-5 flex-none text-water-blue" aria-label="Selected" />
            ) : null}
          </div>
          <p className="mt-1.5 text-sm leading-6 text-water-muted">{option.description}</p>
        </div>
      </div>
    </button>
  )
}

function ResultFact({ label, value }) {
  return (
    <div className="border-l-2 border-water-blue/35 pl-4">
      <dt className="text-[0.8125rem] font-semibold uppercase tracking-[0.08em] text-water-muted">{label}</dt>
      <dd className="mt-1 text-sm font-medium leading-6 text-water-ink">{value}</dd>
    </div>
  )
}

function FinderSystemStrip() {
  return (
    <div className="mt-8 hidden grid-cols-3 gap-2 sm:grid lg:grid">
      {FINDER_SYSTEMS.map((system) => (
        <div key={system.label} className="overflow-hidden rounded-[0.875rem] border border-white/15 bg-white">
          <div className="relative aspect-square bg-white p-2">
            <Image
              src={system.src}
              alt={system.alt}
              fill
              className="object-contain p-2"
              sizes="(min-width: 1024px) 110px, 28vw"
            />
          </div>
          <p className="border-t border-white/15 bg-white/10 px-2 py-2 text-center text-[0.8125rem] font-semibold text-water-ink">
            {system.label}
          </p>
        </div>
      ))}
    </div>
  )
}

function StepRail({ stepIndex }) {
  return (
    <ol className="mt-8 hidden gap-3 lg:grid" aria-label="Product finder progress">
      {QUESTION_STEPS.map((step, index) => {
        const complete = stepIndex > index
        const active = stepIndex === index

        return (
          <li key={step.id} className="flex items-center gap-3">
            <span
              className={`flex h-8 w-8 flex-none items-center justify-center rounded-full border text-sm font-semibold tabular-nums ${
                complete
                  ? 'border-water-teal bg-water-teal text-water-ink'
                  : active
                    ? 'border-white bg-white text-water-ink'
                    : 'border-white/25 text-white/65'
              }`}
              aria-hidden="true"
            >
              {complete ? '✓' : index + 1}
            </span>
            <span className={`text-sm font-medium ${complete || active ? 'text-white' : 'text-white/55'}`}>
              {step.title}
            </span>
          </li>
        )
      })}
    </ol>
  )
}

function getNextButtonLabel(stepIndex) {
  if (stepIndex === 0) return 'Next: Water Source'
  if (stepIndex === 1) return 'Next: Treatment Scope'
  if (stepIndex === 2) return 'Next: Household Size'
  return 'View My Recommendation'
}

function getQuizWaterIssue(answers) {
  if (answers.concerns.includes('bacteria')) return answers.waterSource === 'well' ? 'well-water' : 'not-sure'
  if (answers.concerns.includes('staining')) return 'staining-issues'
  if (answers.concerns.includes('scale')) return 'hard-water'
  if (answers.concerns.includes('chlorine')) return 'chlorine'
  if (answers.concerns.includes('drinking')) return 'drinking-water'
  return 'not-sure'
}

function normalizeLocationContextValue(value) {
  return typeof value === 'string' ? value.trim() : ''
}

export function getHardnessFollowUpHref(answers, locationContext = {}) {
  const hasCityScaleResult =
    answers.waterSource === 'city' && answers.concerns?.includes('scale')
  const origin = normalizeLocationContextValue(locationContext.origin)

  if (!hasCityScaleResult || origin === 'water-hardness') return null

  const province = normalizeLocationContextValue(locationContext.province)
  const city = normalizeLocationContextValue(locationContext.city)
  const params = new URLSearchParams()

  if (province) params.set('province', province)
  if (province && city) params.set('city', city)

  const query = params.toString()
  return `/water-hardness${query ? `?${query}` : ''}`
}

export function readInitialProgress() {
  const params = new URLSearchParams(window.location.search)
  const locationContext = {
    province: normalizeLocationContextValue(params.get('province')),
    city: normalizeLocationContextValue(params.get('city')),
    origin: normalizeLocationContextValue(params.get('wd_from')),
  }
  const queryAnswers = sanitizeQuizAnswers({
    concerns: params.get('wd_concerns')?.split(',').filter(Boolean) || [],
    waterSource: params.get('wd_source') || '',
    coverage: params.get('wd_coverage') || '',
    household: params.get('wd_household') || '',
  })
  const hasQueryAnswers = Object.values(queryAnswers).some((value) => Array.isArray(value) ? value.length > 0 : Boolean(value))
  let answers = queryAnswers
  let requestedStep = Number.parseInt(params.get('wd_step') || '0', 10)

  if (!hasQueryAnswers) {
    try {
      const saved = JSON.parse(window.sessionStorage.getItem(QUIZ_STORAGE_KEY) || 'null')
      if (saved?.version === QUIZ_VERSION) {
        answers = sanitizeQuizAnswers(saved.answers)
        requestedStep = Number.parseInt(saved.stepIndex || 0, 10)
      }
    } catch {
      // A blocked or malformed session store cannot prevent the questionnaire.
    }
  }

  return {
    answers,
    stepIndex: Math.min(Math.max(Number.isFinite(requestedStep) ? requestedStep : 0, 0), maxReachableStep(answers)),
    locationContext,
  }
}

const subscribeHydration = () => () => {}
const getClientHydration = () => true
const getServerHydration = () => false

export function HelpMeChooseExperience({ shopifyProductsByHandle = {} }) {
  const hydrated = useSyncExternalStore(subscribeHydration, getClientHydration, getServerHydration)
  return <HelpMeChooseExperienceContent
    key={hydrated ? 'restored' : 'server'}
    restoreOnMount={hydrated}
    shopifyProductsByHandle={shopifyProductsByHandle}
  />
}

function HelpMeChooseExperienceContent({ restoreOnMount, shopifyProductsByHandle }) {
  const [initialProgress] = useState(() => restoreOnMount ? readInitialProgress() : null)
  const [stepIndex, setStepIndex] = useState(() => initialProgress?.stepIndex ?? 0)
  const [answers, setAnswers] = useState(() => initialProgress?.answers ?? EMPTY_ANSWERS)
  const hasRestoredProgress = Boolean(initialProgress)
  const locationContext = initialProgress?.locationContext ?? {}
  const quizHeadingRef = useRef(null)
  const trackedStartRef = useRef(false)

  const currentStep = QUESTION_STEPS[stepIndex]
  const result = stepIndex >= QUESTION_STEPS.length ? buildResult(answers) : null
  const concernLimitReached = answers.concerns.length >= 2
  const browseLinks = result
    ? [result.primary.browseKey, result.secondary?.browseKey]
        .filter(Boolean)
        .filter((key, index, list) => list.indexOf(key) === index)
        .map((key) => BROWSE_LINKS[key])
        .filter(Boolean)
    : []
  const resultId = result
    ? [result.primary.shopifyHandle || result.primary.title, result.planType === 'paired' ? result.secondary?.shopifyHandle || result.secondary?.title : null]
        .filter(Boolean)
        .join('+')
    : ''
  const hasResult = Boolean(result)
  const hardnessFollowUpHref = result
    ? getHardnessFollowUpHref(answers, locationContext)
    : null

  useEffect(() => {
    if (!hasRestoredProgress) return
    try {
      window.sessionStorage.setItem(QUIZ_STORAGE_KEY, JSON.stringify({
        version: QUIZ_VERSION,
        answers,
        stepIndex,
      }))
    } catch {
      // URL state still preserves progress when session storage is blocked.
    }

    const url = new URL(window.location.href)
    for (const key of ['wd_concerns', 'wd_source', 'wd_coverage', 'wd_household', 'wd_step']) {
      url.searchParams.delete(key)
    }
    if (answers.concerns.length) url.searchParams.set('wd_concerns', answers.concerns.join(','))
    if (answers.waterSource) url.searchParams.set('wd_source', answers.waterSource)
    if (answers.coverage) url.searchParams.set('wd_coverage', answers.coverage)
    if (answers.household) url.searchParams.set('wd_household', answers.household)
    if (stepIndex > 0) url.searchParams.set('wd_step', String(stepIndex))
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`)
  }, [answers, hasRestoredProgress, stepIndex])

  useEffect(() => {
    if (!hasResult) return undefined

    document.body.dataset.mobileStickyCtaActive = 'true'

    return () => {
      delete document.body.dataset.mobileStickyCtaActive
    }
  }, [hasResult])

  function trackStart() {
    if (trackedStartRef.current) return
    trackedStartRef.current = true
    TrackingEvents.trackDiagnosticTools('help_me_choose_start', {
      tool: 'help-me-choose',
    })
  }

  function toggleConcern(id) {
    trackStart()
    setAnswers((current) => {
      if (id === 'not-sure') {
        return {
          ...current,
          concerns: current.concerns.includes('not-sure') ? [] : ['not-sure'],
        }
      }

      const concerns = current.concerns.filter((item) => item !== 'not-sure')

      if (concerns.includes(id)) {
        return {
          ...current,
          concerns: concerns.filter((item) => item !== id),
        }
      }

      if (concerns.length >= 2) {
        return current
      }

      return {
        ...current,
        concerns: [...concerns, id],
      }
    })
  }

  function updateAnswer(key, value) {
    trackStart()
    setAnswers((current) => ({
      ...current,
      [key]: value,
    }))
  }

  function goNext() {
    if (stepIndex < QUESTION_STEPS.length) {
      TrackingEvents.trackDiagnosticTools('help_me_choose_step', {
        tool: 'help-me-choose',
        step: currentStep.id,
        step_number: stepIndex + 1,
      })

      if (stepIndex === QUESTION_STEPS.length - 1) {
        const completedResult = buildResult(answers)
        TrackingEvents.trackDiagnosticTools('help_me_choose_result', {
          tool: 'help-me-choose',
          result: completedResult.primary.shopifyHandle || completedResult.primary.title,
          secondary_result: completedResult.secondary?.shopifyHandle || completedResult.secondary?.title || null,
          assessment_priority: completedResult.assessmentPriority,
          plan_type: completedResult.planType || 'single',
        })
      }

      setStepIndex((current) => current + 1)
      requestAnimationFrame(() => quizHeadingRef.current?.focus())
    }
  }

  function goBack() {
    setStepIndex((current) => Math.max(current - 1, 0))
    requestAnimationFrame(() => quizHeadingRef.current?.focus())
  }

  function resetQuiz() {
    TrackingEvents.trackDiagnosticTools('help_me_choose_restart', {
      tool: 'help-me-choose',
      previous_step: stepIndex,
    })
    setAnswers(EMPTY_ANSWERS)
    setStepIndex(0)
    trackedStartRef.current = false
    try {
      window.sessionStorage.removeItem(QUIZ_STORAGE_KEY)
    } catch {
      // The URL update below clears progress when session storage is blocked.
    }
    requestAnimationFrame(() => quizHeadingRef.current?.focus())
  }

  function canContinue() {
    if (currentStep.id === 'concerns') return answers.concerns.length > 0
    if (currentStep.id === 'waterSource') return Boolean(answers.waterSource)
    if (currentStep.id === 'coverage') return Boolean(answers.coverage)
    if (currentStep.id === 'household') return Boolean(answers.household)
    return false
  }

  return (
    <main id="main-content" className="bg-water-surface">
      <section className="border-b border-water-border bg-water-canvas">
        <div className="grid w-full lg:min-h-[calc(100svh-7.25rem)] lg:grid-cols-[minmax(20rem,0.72fr)_minmax(0,1.45fr)]">
          <aside className="bg-water-ink px-6 py-8 text-white sm:px-8 sm:py-12 lg:px-10 lg:py-14 xl:px-14">
            <div className="mx-auto max-w-xl lg:sticky lg:top-24">
              <p className="flex items-center gap-2 text-[0.8125rem] font-semibold uppercase tracking-[0.1em] text-water-teal">
                <span className="h-2 w-2 rounded-full bg-water-teal" aria-hidden="true" />
                Water Doctor Product Finder
              </p>
              <h1 className="mt-4 text-balance text-[2.25rem] font-semibold leading-[0.98] tracking-[-0.045em] sm:mt-5 sm:text-5xl lg:text-[3.35rem]">
                Tell us what’s happening. We’ll narrow the system.
              </h1>
              <p className="mt-4 max-w-lg text-sm leading-6 text-white/72 sm:mt-6 sm:text-lg sm:leading-8">
                Answer 4 practical questions and get a best-fit category or a clear signal that expert confirmation should come first.
              </p>

              <ul className="mt-7 hidden gap-3 text-sm leading-6 text-white/80 sm:grid sm:grid-cols-3 lg:grid-cols-1">
                {['About 45 seconds', 'Real Water Doctor products', 'No obligation to submit'].map((item) => (
                  <li key={item} className="flex items-center gap-2.5">
                    <CheckCircleIcon className="h-5 w-5 flex-none text-water-teal" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <StepRail stepIndex={stepIndex} />
              {stepIndex < QUESTION_STEPS.length ? <FinderSystemStrip /> : null}

              <div className="mt-6 border-t border-white/15 pt-4 sm:mt-8 sm:pt-6">
                <p className="hidden text-sm leading-6 text-white/65 sm:block">Already know you need sizing or installation advice?</p>
                <Link
                  href="/forms/home-assessment"
                  className="inline-flex min-h-11 items-center gap-2 font-semibold text-white underline decoration-white/30 decoration-2 underline-offset-4 transition-colors hover:text-water-teal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-teal focus-visible:ring-offset-4 focus-visible:ring-offset-water-ink sm:mt-2"
                >
                  Skip to Free Assessment
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </aside>

          <div id="quiz" className="bg-white px-5 py-8 sm:px-8 sm:py-10 lg:px-10 lg:py-12 xl:px-14">
              <div className="mx-auto w-full max-w-5xl overflow-hidden">
                <div className="border-b border-water-border pb-6">
                  <div className="flex items-center justify-between gap-6">
                    <div>
                      <p className="text-[0.8125rem] font-semibold uppercase tracking-[0.08em] text-water-blue-dark tabular-nums">
                        {stepIndex < QUESTION_STEPS.length ? `Question ${stepIndex + 1} of ${QUESTION_STEPS.length}` : 'Recommendation Ready'}
                      </p>
                      {stepIndex < QUESTION_STEPS.length ? (
                        <h2 ref={quizHeadingRef} tabIndex={-1} className="mt-2 max-w-3xl text-balance text-3xl font-semibold leading-tight tracking-[-0.035em] text-water-ink focus:outline-none sm:text-4xl">
                          {QUESTION_STEPS[stepIndex].title}
                        </h2>
                      ) : (
                        <p ref={quizHeadingRef} tabIndex={-1} className="mt-2 max-w-3xl text-xl font-semibold tracking-[-0.02em] text-water-ink focus:outline-none">
                          {result?.expertFirst && !result.secondary
                            ? 'Review why expert confirmation comes first, then send the essentials in one short form.'
                            : 'Review the fit, compare the real products, then choose whether to shop or ask us to confirm it.'}
                        </p>
                      )}
                    </div>
                    {stepIndex > 0 || answers.concerns.length > 0 ? (
                      <button
                        type="button"
                        onClick={resetQuiz}
                        className="inline-flex min-h-11 flex-none items-center gap-2 rounded-full border border-water-border bg-white px-4 py-2 text-sm font-semibold text-water-ink transition-colors hover:border-water-blue/35 hover:bg-water-canvas focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue focus-visible:ring-offset-2"
                      >
                        <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />
                        Start Over
                      </button>
                    ) : null}
                  </div>

                  {stepIndex < QUESTION_STEPS.length ? (
                    <div
                      className="mt-6 h-1.5 w-full overflow-hidden rounded-full bg-water-canvas"
                      role="progressbar"
                      aria-label="Product finder progress"
                      aria-valuemin={1}
                      aria-valuemax={QUESTION_STEPS.length}
                      aria-valuenow={stepIndex + 1}
                      aria-valuetext={`Question ${stepIndex + 1} of ${QUESTION_STEPS.length}`}
                    >
                      <div
                        className="h-full rounded-full bg-water-blue transition-[width] duration-300 motion-reduce:transition-none"
                        style={{
                          width: `${((Math.min(stepIndex, QUESTION_STEPS.length - 1) + 1) / QUESTION_STEPS.length) * 100}%`,
                        }}
                      />
                    </div>
                  ) : (
                    <div className="mt-5 inline-flex rounded-full bg-water-aqua px-3 py-1.5 text-[0.8125rem] font-semibold text-water-blue-dark">
                      4 Questions Complete
                    </div>
                  )}
                </div>

                {stepIndex < QUESTION_STEPS.length ? (
                  <div className="py-7 sm:py-8">
                    <p className="max-w-2xl text-base leading-7 text-water-muted">
                      {currentStep.description}
                    </p>

                    {currentStep.id === 'concerns' ? (
                      <>
                        <div className="mt-7 grid gap-4 sm:grid-cols-2">
                          {CONCERN_OPTIONS.map((option) => (
                            <QuizOptionCard
                              key={option.id}
                              option={option}
                              selected={answers.concerns.includes(option.id)}
                              disabled={
                                !answers.concerns.includes(option.id) &&
                                concernLimitReached &&
                                !answers.concerns.includes('not-sure')
                              }
                              onClick={() => toggleConcern(option.id)}
                            />
                          ))}
                        </div>
                        <p className="mt-5 text-sm text-water-muted" aria-live="polite">
                          {answers.concerns.includes('not-sure')
                            ? 'You chose the uncertainty-safe path. The guide will stay more conservative.'
                            : concernLimitReached
                              ? 'You have picked two concerns, which is enough to keep the result focused.'
                              : 'Choose one or two concerns.'}
                        </p>
                      </>
                    ) : null}

                    {currentStep.id === 'waterSource' ? (
                      <div className="mt-7 grid gap-4 sm:grid-cols-3">
                        {WATER_SOURCE_OPTIONS.map((option) => (
                          <QuizOptionCard
                            key={option.id}
                            option={option}
                            selected={answers.waterSource === option.id}
                            onClick={() => updateAnswer('waterSource', option.id)}
                          />
                        ))}
                      </div>
                    ) : null}

                    {currentStep.id === 'coverage' ? (
                      <div className="mt-7 grid gap-4">
                        {COVERAGE_OPTIONS.map((option) => (
                          <QuizOptionCard
                            key={option.id}
                            option={option}
                            selected={answers.coverage === option.id}
                            onClick={() => updateAnswer('coverage', option.id)}
                          />
                        ))}
                      </div>
                    ) : null}

                    {currentStep.id === 'household' ? (
                      <div className="mt-7 grid gap-4 sm:grid-cols-3">
                        {HOUSEHOLD_OPTIONS.map((option) => (
                          <QuizOptionCard
                            key={option.id}
                            option={option}
                            selected={answers.household === option.id}
                            onClick={() => updateAnswer('household', option.id)}
                          />
                        ))}
                      </div>
                    ) : null}

                    <div className="mt-8 flex flex-col gap-3 border-t border-water-border pt-6 sm:flex-row sm:items-center sm:justify-between">
                      {stepIndex > 0 ? (
                        <button
                          type="button"
                          onClick={goBack}
                          className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full border border-water-border px-5 py-3 text-sm font-semibold text-water-ink transition-colors hover:border-water-blue/35 hover:bg-water-canvas focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue focus-visible:ring-offset-2"
                        >
                          <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
                          Previous Question
                        </button>
                      ) : (
                        <p className="text-sm text-water-muted">Choose 1 or 2 concerns to begin.</p>
                      )}
                      <button
                        type="button"
                        onClick={goNext}
                        disabled={!canContinue()}
                        className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-water-blue px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-water-blue-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-water-border disabled:text-water-muted"
                      >
                        {getNextButtonLabel(stepIndex)}
                        <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="pb-24 pt-7 sm:py-8 lg:pb-8">
                    <div className="border-b border-water-border pb-7">
                      <div className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr] lg:items-start">
                        <div>
                          <p className="text-[0.8125rem] font-semibold uppercase tracking-[0.08em] text-water-blue-dark">
                            Why this path fits
                          </p>
                          <h2 className="mt-3 text-balance text-3xl font-semibold leading-tight tracking-[-0.035em] text-water-ink sm:text-4xl">
                            {result.headline}
                          </h2>
                          <p className="mt-4 max-w-3xl text-base leading-7 text-water-muted">
                            {result.summary}
                          </p>
                          <p className="mt-5 max-w-3xl border-l-2 border-water-teal bg-water-sand px-4 py-3 text-sm leading-6 text-water-muted">
                            This is a starting recommendation based on the answers you selected. It does not replace a water test, peak-flow check, or installation review.
                          </p>
                        </div>

                        <div>
                          <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
                            <ResultFact
                              label="Concerns"
                              value={answers.concerns.map(getConcernLabel).join(' + ')}
                            />
                            <ResultFact
                              label="Water source"
                              value={WATER_SOURCE_OPTIONS.find((item) => item.id === answers.waterSource)?.title || answers.waterSource}
                            />
                            <ResultFact
                              label="Coverage"
                              value={COVERAGE_OPTIONS.find((item) => item.id === answers.coverage)?.title || answers.coverage}
                            />
                            <ResultFact
                              label="Household"
                              value={HOUSEHOLD_OPTIONS.find((item) => item.id === answers.household)?.title || answers.household}
                            />
                          </dl>

                          {hardnessFollowUpHref ? (
                            <TrackedToolLink
                              href={hardnessFollowUpHref}
                              tool="water-hardness"
                              placement="help-me-choose-result"
                              intent="city-scale-confirmation"
                              pagePath="/landings/help-me-choose"
                              className="mt-4 inline-flex min-h-11 items-center gap-2 rounded-md text-sm font-semibold text-water-blue-dark underline decoration-water-blue/25 decoration-2 underline-offset-4 transition-colors hover:text-water-blue focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue focus-visible:ring-offset-2"
                            >
                              Check Local Hardness
                              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
                            </TrackedToolLink>
                          ) : null}
                        </div>
                      </div>
                    </div>

                    <div className="mt-7 grid gap-5 lg:grid-cols-[1.15fr_0.85fr]">
                      {result.expertFirst ? (
                        <div className="rounded-[1.375rem] bg-water-ink p-6 text-white sm:p-8">
                          <p className="text-[0.8125rem] font-semibold uppercase tracking-[0.08em] text-water-teal">Expert Review First</p>
                          <h3 className="mt-3 text-balance text-3xl font-semibold tracking-[-0.035em]">Avoid a premature equipment choice.</h3>
                          <p className="mt-4 text-base leading-7 text-white/72">{result.primaryReason}</p>
                          <ul className="mt-6 grid gap-3 text-sm leading-6 text-white/80">
                            <li className="flex gap-2.5"><CheckCircleIcon className="mt-0.5 h-5 w-5 flex-none text-water-teal" aria-hidden="true" />Confirm the water source and symptom pattern.</li>
                            <li className="flex gap-2.5"><CheckCircleIcon className="mt-0.5 h-5 w-5 flex-none text-water-teal" aria-hidden="true" />Review sizing and installation constraints.</li>
                            <li className="flex gap-2.5"><CheckCircleIcon className="mt-0.5 h-5 w-5 flex-none text-water-teal" aria-hidden="true" />Compare products only after the category is clear.</li>
                          </ul>
                        </div>
                      ) : (
                        <ProductCard
                          product={result.primary}
                          reason={result.primaryReason}
                          tone="primary"
                          heading={result.primaryRole}
                          shopifyProduct={
                            result.primary.shopifyHandle
                              ? shopifyProductsByHandle[result.primary.shopifyHandle]
                              : null
                          }
                        />
                      )}

                      <div className="grid gap-5">
                        <div id="result-assessment" className="order-1 scroll-mt-24 rounded-[1.375rem] border border-water-blue/25 bg-water-canvas p-6">
                          <p className="text-[0.8125rem] font-semibold uppercase tracking-[0.08em] text-water-blue">
                            Recommended next step
                          </p>
                          <p className="mt-3 text-2xl font-semibold tracking-[-0.025em] text-water-ink">
                            {result.assessmentPriority === 'high'
                              ? 'Strongly recommended before purchase'
                              : result.assessmentPriority === 'recommended'
                                ? 'Recommended before final sizing'
                                : 'Usually safe to shop directly'}
                          </p>
                          <p className="mt-4 text-base leading-7 text-water-muted">
                            {result.assessmentCopy}
                          </p>
                          <p className="mt-4 text-sm leading-6 text-water-muted">
                            {result.pairingCopy ||
                              'Use the product pages for self-education first. If the fit still feels uncertain, move to the free assessment instead of guessing at checkout.'}
                          </p>

                          <div className="mt-6 border-t border-water-border pt-6">
                            <QuickAssessment
                              key={resultId}
                              mode="residential"
                              service="product-guidance"
                              pagePath="/landings/help-me-choose"
                              placement="result"
                              contentId={resultId}
                              ctaVariant={`help-me-choose-${result.assessmentPriority}`}
                              initialWaterIssue={getQuizWaterIssue(answers)}
                              diagnostic={{
                                tool: 'help-me-choose',
                                concerns: answers.concerns,
                                waterSource: answers.waterSource,
                                coverage: answers.coverage,
                                household: answers.household,
                                resultId,
                                assessmentPriority: result.assessmentPriority,
                                version: '2026-08',
                              }}
                              submitLabel="Request Free Assessment"
                            />
                          </div>

                          {browseLinks.length > 0 ? (
                            <div className="mt-6 border-t border-water-border pt-5">
                              <p className="text-sm font-semibold text-water-ink">Browse the wider category first</p>
                              <div className="mt-3 flex flex-col gap-3">
                                {browseLinks.map((link) => (
                                  <a
                                    key={link.href}
                                    href={link.href}
                                    className="inline-flex min-h-11 items-center justify-center rounded-full border border-water-border bg-white px-4 py-2.5 text-sm font-semibold text-water-ink transition-colors hover:border-water-blue/35 hover:text-water-blue-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue focus-visible:ring-offset-2"
                                  >
                                    {link.label}
                                  </a>
                                ))}
                              </div>
                            </div>
                          ) : null}
                        </div>

                        {result.secondary ? (
                          <div className="order-2">
                            <ProductCard
                              product={result.secondary}
                              reason={result.secondaryReason}
                              tone="backup"
                              compact
                              heading={result.secondaryRole}
                              shopifyProduct={
                                result.secondary.shopifyHandle
                                  ? shopifyProductsByHandle[result.secondary.shopifyHandle]
                                  : null
                              }
                            />
                          </div>
                        ) : null}
                      </div>
                    </div>

                    <div
                      data-mobile-sticky-cta
                      className="fixed inset-x-0 bottom-0 z-40 border-t border-water-border bg-white/96 px-4 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-3 shadow-[0_-10px_30px_rgba(11,34,57,0.10)] backdrop-blur lg:hidden"
                    >
                      <a
                        href="#result-assessment"
                        className="inline-flex min-h-12 w-full items-center justify-center rounded-full bg-water-blue px-5 py-3 text-sm font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue focus-visible:ring-offset-2"
                      >
                        Ask Water Doctor to Confirm Fit
                      </a>
                    </div>
                  </div>
                )}
              </div>
          </div>
        </div>
      </section>

      <section className="border-t border-water-border bg-water-surface py-14 sm:py-18">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-[0.8125rem] font-semibold uppercase tracking-[0.08em] text-water-blue-dark">
              Common questions
            </p>
            <h2 className="mt-3 text-balance text-3xl font-semibold tracking-[-0.035em] text-water-ink sm:text-4xl">
              Before you choose, keep these in mind.
            </h2>
          </div>

          <div className="mt-8 divide-y divide-water-border border-y border-water-border">
            {FAQS.map((faq) => (
              <details
                key={faq.question}
                className="group bg-transparent"
              >
                <summary className="flex min-h-16 cursor-pointer list-none items-center justify-between gap-5 py-5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue focus-visible:ring-offset-2 [&::-webkit-details-marker]:hidden">
                  <span className="text-lg font-semibold tracking-[-0.01em] text-water-ink">{faq.question}</span>
                  <ChevronDownIcon className="h-5 w-5 flex-none text-water-muted transition-transform duration-200 group-open:rotate-180 motion-reduce:transition-none" aria-hidden="true" />
                </summary>
                <p className="max-w-3xl pb-6 pr-10 text-base leading-7 text-water-muted">{faq.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </main>
  )
}
